import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { makeNetTerms, netTermsToDays } from '~/infra/translator';
import { assertUnreachable, notEmpty } from '~/infra/utils';
import { PracticeManagementServiceIntegrationModel, serviceModel } from '~/models';
import { fromDTO as billingFromDTO, toDTO as billingToDTO } from '~/models/service-billing/translator';
import { fromDTO as costFromDTO, toDTO as costToDTO } from '~/models/service-cost/translator';
import { PendingAmendmentModel } from '../PendingAmendmentModel';
import { AmendmentDraftChangeType, makeServiceAddedChange, makeServiceBilledUponChange } from './amendment';
export function fromDTO(dto) {
    var _dto_inProgressAmendments, _dto_amendment_draftVersion;
    return _object_spread_props(_object_spread({}, dto), {
        legalTermsDocuments: dto.legalTermsDocuments || [],
        serviceBundles: dto.serviceBundles ? {
            bundles: dto.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map(serviceModel.fromDTO).filter(notEmpty)
                }))
        } : undefined,
        services: dto.services.map(serviceModel.fromDTO).filter(notEmpty),
        pendingAmendments: dto.pendingAmendments.map(PendingAmendmentModel.fromDTO),
        inProgressAmendments: ((_dto_inProgressAmendments = dto.inProgressAmendments) !== null && _dto_inProgressAmendments !== void 0 ? _dto_inProgressAmendments : []).map(PendingAmendmentModel.fromDTO),
        practiceManagementSyncFailures: dto.practiceManagementSyncFailures ? dto.practiceManagementSyncFailures.map(PracticeManagementServiceIntegrationModel.fromDTO) : undefined,
        amendment: dto.amendment ? {
            draftChanges: dto.amendment.draftChanges.map(amendmentDraftChangeFromDTO).filter(notEmpty),
            draftVersion: (_dto_amendment_draftVersion = dto.amendment.draftVersion) !== null && _dto_amendment_draftVersion !== void 0 ? _dto_amendment_draftVersion : ''
        } : undefined
    });
}
export function toDTO(_0) {
    let { appliedTax: _appliedTax } = _0, relationship = _object_without_properties(_0, [
        "appliedTax"
    ]);
    return _object_spread_props(_object_spread({}, relationship), {
        serviceBundles: relationship.serviceBundles ? {
            bundles: relationship.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map(serviceModel.toDTO).filter(notEmpty)
                }))
        } : undefined,
        services: relationship.services.map(serviceModel.toDTO).filter(notEmpty),
        pendingAmendments: relationship.pendingAmendments.map((p)=>p.toDTO()),
        inProgressAmendments: relationship.inProgressAmendments.map((p)=>p.toDTO()),
        practiceManagementSyncFailures: relationship.practiceManagementSyncFailures ? relationship.practiceManagementSyncFailures.map((p)=>p.toJSON()) : undefined,
        amendment: relationship.amendment ? {
            draftChanges: relationship.amendment.draftChanges.map(amendmentDraftChangeToDTO),
            draftVersion: relationship.amendment.draftVersion
        } : undefined
    });
}
export function amendmentDraftChangeToDTO(change) {
    switch(change.type){
        case AmendmentDraftChangeType.ServiceCost:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    cost: costToDTO({
                        cost: change.value.cost,
                        shouldResetDiscount: false
                    })
                }
            };
        case AmendmentDraftChangeType.NetTerms:
            return {
                type: change.type,
                value: {
                    netTerms: netTermsToDays(change.value.netTerms)
                }
            };
        case AmendmentDraftChangeType.ServiceBilledUpon:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    billedUpon: billingToDTO(change.value.billedUpon)
                }
            };
        case AmendmentDraftChangeType.ServiceBundle:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    bundle: {
                        services: change.value.bundle.services.map(serviceModel.includedServiceToDTO)
                    }
                }
            };
        case AmendmentDraftChangeType.ServiceAdded:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    service: serviceModel.toDTO(change.value.service)
                }
            };
        case AmendmentDraftChangeType.ServiceName:
        case AmendmentDraftChangeType.ServiceDescription:
        case AmendmentDraftChangeType.ServiceTermsUpdated:
        case AmendmentDraftChangeType.ServiceTermsRemoved:
        case AmendmentDraftChangeType.ServiceTerminated:
        case AmendmentDraftChangeType.ServiceCanceled:
        case AmendmentDraftChangeType.ServiceIntegratedClass:
        case AmendmentDraftChangeType.ManualPayments:
        case AmendmentDraftChangeType.CoverCreditCardFees:
        case AmendmentDraftChangeType.LegalTermsDocuments:
        case AmendmentDraftChangeType.AmendmentsApprovalMethod:
        case AmendmentDraftChangeType.InvoicesApprovalMethod:
        case AmendmentDraftChangeType.CustomPageAdded:
        case AmendmentDraftChangeType.CustomPageUpdated:
            return change;
        default:
            return assertUnreachable(change);
    }
}
export function amendmentDraftChangeFromDTO(dto) {
    switch(dto.type){
        case AmendmentDraftChangeType.ServiceCost:
            return {
                type: dto.type,
                value: {
                    serviceId: dto.value.serviceId,
                    cost: costFromDTO(dto.value.cost)
                }
            };
        case AmendmentDraftChangeType.NetTerms:
            return {
                type: dto.type,
                value: {
                    netTerms: makeNetTerms(dto.value.netTerms)
                }
            };
        case AmendmentDraftChangeType.ServiceBilledUpon:
            {
                const billing = billingFromDTO(dto.value.billedUpon);
                return billing ? makeServiceBilledUponChange(dto.value.serviceId, billing) : undefined;
            }
        case AmendmentDraftChangeType.ServiceBundle:
            return {
                type: dto.type,
                value: {
                    serviceId: dto.value.serviceId,
                    bundle: {
                        services: dto.value.bundle.services.map(serviceModel.includedServiceFromDTO)
                    }
                }
            };
        case AmendmentDraftChangeType.ServiceAdded:
            {
                const service = serviceModel.fromDTO(dto.value.service);
                return service ? makeServiceAddedChange(dto.value.serviceId, service) : undefined;
            }
        case AmendmentDraftChangeType.ServiceName:
        case AmendmentDraftChangeType.ServiceDescription:
        case AmendmentDraftChangeType.ServiceTermsUpdated:
        case AmendmentDraftChangeType.ServiceTermsRemoved:
        case AmendmentDraftChangeType.ServiceTerminated:
        case AmendmentDraftChangeType.ServiceCanceled:
        case AmendmentDraftChangeType.ServiceIntegratedClass:
        case AmendmentDraftChangeType.ManualPayments:
        case AmendmentDraftChangeType.CoverCreditCardFees:
        case AmendmentDraftChangeType.LegalTermsDocuments:
        case AmendmentDraftChangeType.AmendmentsApprovalMethod:
        case AmendmentDraftChangeType.InvoicesApprovalMethod:
        case AmendmentDraftChangeType.CustomPageAdded:
        case AmendmentDraftChangeType.CustomPageUpdated:
            return dto;
        default:
            return assertUnreachable(dto);
    }
}
